import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Currency, CurrencyAmount, Token, TradeType } from '@pancakeswap/sdk'
import { ChainId } from '@pancakeswap/chains'

import { Box, BscScanIcon, Flex, InjectedModalProps, Link } from '@pancakeswap/uikit'
import {
  ApproveModalContent,
  SwapPendingModalContent,
  SwapTransactionReceiptModalContent,
} from '@pancakeswap/widgets-internal'
import { useTranslation } from '@pancakeswap/localization'
import { SmartRouterTrade } from '@pancakeswap/smart-router/evm'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { getBlockExploreLink, getBlockExploreName } from 'utils'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { WrappedTokenInfo } from '@pancakeswap/token-lists'
import truncateHash from '@pancakeswap/utils/truncateHash'

import { Field } from 'state/swap/actions'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useUserSlippage } from '@pancakeswap/utils/user'
import { useSwapState } from 'state/swap/hooks'
import { ApprovalState } from 'hooks/useApproveCallback'
import { useDebounce } from '@pancakeswap/hooks'
import AddToWalletButton, { AddToWalletTextOptions } from 'components/AddToWallet/AddToWalletButton'
import getTokenLogoURL from 'utils/getTokenLogoURL'
import { ConfirmModalState, PendingConfirmModalState } from '../types'

import ConfirmSwapModalContainer from '../../components/ConfirmSwapModalContainer'
import { SwapTransactionErrorContent } from '../../components/SwapTransactionErrorContent'
import { TransactionConfirmSwapContent } from '../components/TransactionConfirmSwapContent'
import { ApproveStepFlow } from './ApproveStepFlow'
import { useWallchainStatus } from '../hooks/useWallchain'

interface ConfirmSwapModalProps {
  isMM?: boolean
  isRFQReady?: boolean
  trade?: SmartRouterTrade<TradeType>
  originalTrade?: SmartRouterTrade<TradeType>
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  attemptingTxn: boolean
  txHash?: string
  approval: ApprovalState
  swapErrorMessage?: string | boolean
  showApproveFlow: boolean
  confirmModalState: ConfirmModalState
  startSwapFlow: () => void
  pendingModalSteps: PendingConfirmModalState[]
  currentAllowance: CurrencyAmount<Currency>
  onAcceptChanges: () => void
  customOnDismiss?: () => void
  openSettingModal?: () => void
  isQuickBuy?: boolean
  setIsQuickBuyLoading?: (loading: boolean) => void
}

export const ConfirmSwapModal = memo<InjectedModalProps & ConfirmSwapModalProps>(function ConfirmSwapModalComp({
  isMM,
  trade,
  txHash,
  confirmModalState,
  startSwapFlow,
  pendingModalSteps,
  isRFQReady,
  attemptingTxn,
  originalTrade,
  showApproveFlow,
  currencyBalances,
  swapErrorMessage,
  onDismiss,
  onAcceptChanges,
  customOnDismiss,
  openSettingModal,
  isQuickBuy,
  setIsQuickBuyLoading
}) {
  const { chainId } = useActiveChainId()
  const { t } = useTranslation()
  const [allowedSlippage] = useUserSlippage()
  const { recipient } = useSwapState()
  const [wallchainStatus] = useWallchainStatus()
  const isBonus = useDebounce(wallchainStatus === 'found', 500)
  const [hasStartedTransaction, setHasStartedTransaction] = useState(false)

  const token: Token | undefined = wrappedCurrency(trade?.outputAmount?.currency, chainId)

  const handleDismiss = useCallback(() => {
    if (customOnDismiss) {
      setIsQuickBuyLoading?.(false)
      customOnDismiss?.()
    }
    setIsQuickBuyLoading?.(false)
    onDismiss?.()
  }, [customOnDismiss, onDismiss, setIsQuickBuyLoading])

  const handleStartSwap = useCallback(() => {
    if (!hasStartedTransaction) {
      setHasStartedTransaction(true)
      startSwapFlow()
    }
  }, [hasStartedTransaction, startSwapFlow])

  useEffect(() => {
    if (confirmModalState === 4) {
      setHasStartedTransaction(false)
      setIsQuickBuyLoading?.(false)
      handleDismiss()
    }

  }, [confirmModalState, handleDismiss, setIsQuickBuyLoading])


  useEffect(() => {
    if (confirmModalState === ConfirmModalState.COMPLETED && txHash) {
      setHasStartedTransaction(false)
      setIsQuickBuyLoading?.(false)
      handleDismiss()
    }
  }, [confirmModalState, txHash, handleDismiss, setIsQuickBuyLoading])

  // Reset transaction state when modal is dismissed
  useEffect(() => {
    return () => {
      setHasStartedTransaction(false)
    }
  }, [])

  const topModal = useMemo(() => {
    const currencyA = currencyBalances.INPUT?.currency ?? trade?.inputAmount?.currency
    const currencyB = currencyBalances.OUTPUT?.currency ?? trade?.outputAmount?.currency
    const amountA = formatAmount(trade?.inputAmount, 6) ?? ''
    const amountB = formatAmount(trade?.outputAmount, 6) ?? ''

    if (confirmModalState === ConfirmModalState.RESETTING_APPROVAL) {
      return <ApproveModalContent title={t('Reset Approval on USDT')} isMM={isMM} isBonus={isBonus} />
    }

    if (
      showApproveFlow &&
      (confirmModalState === ConfirmModalState.APPROVING_TOKEN ||
        confirmModalState === ConfirmModalState.APPROVE_PENDING)
    ) {
      return (
        <ApproveModalContent
          title={t('Enable spending %symbol%', { symbol: `${trade?.inputAmount?.currency?.symbol}` })}
          isMM={isMM}
          isBonus={isBonus}
        />
      )
    }

    if (swapErrorMessage) {
      return null
      // return (
      //   <Flex width="100%" alignItems="center" height="calc(430px - 73px - 120px)">
      //     <SwapTransactionErrorContent
      //       message={swapErrorMessage}
      //       onDismiss={handleDismiss}
      //       openSettingModal={openSettingModal}
      //     />
      //   </Flex>
      // )
    }

    if (isQuickBuy) {
      return (
        <SwapPendingModalContent
          title={t('Confirm Swap')}
          currencyA={currencyA}
          currencyB={currencyB}
          amountA={amountA}
          amountB={amountB}
        />
      )
      // eslint-disable-next-line no-else-return
    } else if (attemptingTxn) {
      return (
        <SwapPendingModalContent
          title={t('Confirm Swap')}
          currencyA={currencyA}
          currencyB={currencyB}
          amountA={amountA}
          amountB={amountB}
        />
      )
    } else if (attemptingTxn && isQuickBuy) {
      return (
        <SwapPendingModalContent
          title={t('Confirm Swap')}
          currencyA={currencyA}
          currencyB={currencyB}
          amountA={amountA}
          amountB={amountB}
        />
      )
    }

    // if (attemptingTxn) {
    //   return (
    //     <SwapPendingModalContent
    //       title={t('Confirm Swap')}
    //       currencyA={currencyA}
    //       currencyB={currencyB}
    //       amountA={amountA}
    //       amountB={amountB}
    //     />
    //   )
    // }


    const tokenLogoURL = getTokenLogoURL(token)

    if (confirmModalState === ConfirmModalState.PENDING_CONFIRMATION) {
      handleDismiss()
      return null
    }

    if (confirmModalState === ConfirmModalState.COMPLETED && txHash) {
      handleDismiss()
      return null
    }
    // console.log('isMM:', isMM)
    // console.log('trade:', trade)
    // console.log('recipient:', recipient)
    // console.log('isRFQReady:', isRFQReady)
    // console.log('originalTrade:', originalTrade)
    // console.log('allowedSlippage:', allowedSlippage)
    // console.log('currencyBalances:', currencyBalances)
    // console.log('onConfirm:', handleStartSwap)
    // console.log('onAcceptChanges:', onAcceptChanges)

    return (
      <>
        {(trade != null || trade !== undefined) ? (
          <TransactionConfirmSwapContent
            isMM={isMM}
            trade={trade}
            recipient={recipient}
            isRFQReady={isRFQReady}
            originalTrade={originalTrade}
            allowedSlippage={allowedSlippage}
            currencyBalances={currencyBalances}
            onConfirm={handleStartSwap}
            onAcceptChanges={onAcceptChanges}
          />) : (<p>Loading</p>)}
      </>
    )
  }, [
    isMM,
    isBonus,
    trade,
    txHash,
    isRFQReady,
    originalTrade,
    attemptingTxn,
    currencyBalances,
    showApproveFlow,
    swapErrorMessage,
    token,
    // chainId,
    recipient,
    allowedSlippage,
    confirmModalState,
    t,
    handleDismiss,
    handleStartSwap,
    onAcceptChanges,
    // openSettingModal,
    isQuickBuy,
  ])

  const isShowingLoadingAnimation = useMemo(
    () =>
      confirmModalState === ConfirmModalState.RESETTING_APPROVAL ||
      confirmModalState === ConfirmModalState.APPROVING_TOKEN ||
      confirmModalState === ConfirmModalState.APPROVE_PENDING ||
      attemptingTxn,
    [confirmModalState, attemptingTxn],
  )

  if (!chainId) return null

  return (
    <ConfirmSwapModalContainer
      // minHeight="415px"
      width={['100%', '100%', '100%', '367px']}
      hideTitleAndBackground={confirmModalState !== ConfirmModalState.REVIEWING}
      headerPadding={isShowingLoadingAnimation ? '12px 24px 32px 24px !important' : '12px 24px'}
      bodyPadding={isShowingLoadingAnimation ? '0 24px 24px 24px' : '24px'}
      bodyTop={isShowingLoadingAnimation ? '-15px' : '0'}
      handleDismiss={handleDismiss}
    >
      <Box>{topModal}</Box>
      {isShowingLoadingAnimation && !swapErrorMessage && (
        <ApproveStepFlow confirmModalState={confirmModalState} pendingModalSteps={pendingModalSteps} />
      )}
    </ConfirmSwapModalContainer>
  )
})
